import { Head, Link, router } from "@inertiajs/react"
import { Edit, Lock, Plus, ShieldCheck, Trash2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { adminRolesPath, newAdminRolePath } from "@/routes"
import type { BreadcrumbItem, Role } from "@/types"

interface Props {
  roles: Role[]
  can: { create: boolean }
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Roles", href: adminRolesPath() },
]

export default function Index({ roles, can }: Props) {
  const handleDelete = (role: Role) => {
    if (confirm(`Delete the ${role.name} role?`)) {
      router.delete(`/admin/roles/${role.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Roles" />

      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Roles</h1>
            <p className="text-muted-foreground text-sm">
              A role is a set of permissions. Every screen and action in the app
              checks these.
            </p>
          </div>
          {can.create && (
            <Button render={<Link href={newAdminRolePath()} />}>
              <Plus className="mr-2 h-4 w-4" /> Add Role
            </Button>
          )}
        </div>

        <Card>
          <CardContent className="pt-6">
            {roles.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ShieldCheck />
                  </EmptyMedia>
                  <EmptyTitle>No roles</EmptyTitle>
                  <EmptyDescription>
                    Add a role to start granting access.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Role</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Permissions</TableHead>
                    <TableHead className="text-right">Users</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Link
                            href={`/admin/roles/${role.id}`}
                            className="font-medium capitalize hover:underline"
                          >
                            {role.name}
                          </Link>
                          {role.system && (
                            <Badge variant="outline" className="gap-1">
                              <Lock className="h-3 w-3" /> Built-in
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {role.description ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {role.permission_count}
                      </TableCell>
                      <TableCell className="text-right">
                        {role.user_count}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          {role.can.update && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Edit permissions"
                              render={
                                <Link href={`/admin/roles/${role.id}/edit`} />
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {role.can.destroy && (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Delete"
                              onClick={() => handleDelete(role)}
                            >
                              <Trash2 className="text-destructive h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <p className="text-muted-foreground text-sm">
          Built-in roles can&apos;t be renamed or deleted — the app refers to
          them by name — but their permissions are yours to change. A role with
          users on it can&apos;t be deleted.
        </p>
      </div>
    </AppLayout>
  )
}
